import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import cx from 'clsx';
import { differenceInDays } from 'date-fns';
import {
  Card,
  CardMedia,
  CardMediaProps,
  CardProps,
  CardContent,
  CardContentProps,
  Typography,
  TypographyProps
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LicenseStatus } from 'types/enums';

export type Props = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  title?: string;
  subtitle?: string;
  description?: string | null;
  titleProps?: TypographyProps;
  subtitleProps?: TypographyProps;
  descriptionProps?: TypographyProps;
  mediaProps?: CardMediaProps;
  contentContainerProps?: CardContentProps;
  cardProps?: CardProps;
  src?: string;
  href?: string;
  openNew?: boolean;
  status?: LicenseStatus;
  dateExpiration?: Date;
};

type AppLicenseStatusBadgeProps = {
  status: LicenseStatus;
  dateExpiration?: Date;
  className?: string;
};

const STATUS_COLORS = {
  [LicenseStatus.TRIAL_AVAILABLE]: { background: '#EED7FC', color: '#803FA5' },
  [LicenseStatus.TRIAL]: { background: '#CFE1FD', color: '#1558BC' },
  [LicenseStatus.FULL]: { background: '#BAF3DB', color: '#216E4E' },
  [LicenseStatus.EXPIRED]: { background: '#FFD5D2', color: '#AE2E24' },
  [LicenseStatus.AUDITOR]: { background: '#F5E989', color: '#7F5F01' }
};

const AppCard = ({
  title,
  subtitle,
  description,
  src,
  titleProps,
  subtitleProps,
  descriptionProps,
  cardProps,
  mediaProps,
  contentContainerProps,
  openNew,
  href,
  status,
  dateExpiration,
  ...rest
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const handleCardClick = () => {
    if (!href) return;

    if (openNew) window.open(href, '_blank');
    else history.push(href);
  };

  return (
    <div className={classes.root} {...rest}>
      <Card className={classes.card} onClick={handleCardClick} {...cardProps}>
        {src && <CardMedia className={classes.media} image={src} title={title} {...mediaProps} />}
        <CardContent className={classes.content} {...contentContainerProps}>
          {status && <AppLicenseStatusBadge status={status} dateExpiration={dateExpiration} />}
          {title && (
            <Typography variant="h3" {...titleProps}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="subtitle2" className={classes.grey} {...subtitleProps}>
              {subtitle}
            </Typography>
          )}
          {description && (
            <Typography variant="body1" {...descriptionProps}>
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AppLicenseStatusBadge = ({
  status,
  dateExpiration,
  className
}: AppLicenseStatusBadgeProps) => {
  const classes = useStatusBadgeStyles();
  const { t } = useTranslation('LicenseStatus');

  const daysLeft = dateExpiration ? differenceInDays(dateExpiration, new Date()) : 0;
  const statusColors = STATUS_COLORS[status];

  return (
    <div
      className={cx(classes.root, className)}
      style={{ backgroundColor: statusColors.background, borderColor: statusColors.color }}
    >
      <Typography
        variant="subtitle2"
        className={classes.text}
        style={{ color: statusColors.color }}
      >
        {t(`${status}`, { count: Math.max(0, daysLeft) })}
      </Typography>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    cursor: 'pointer',
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      flex: '0 0 30%'
    },
    [theme.breakpoints.up('sm')]: {
      flex: '0 0 50%'
    },
    [theme.breakpoints.up('xs')]: {
      flex: '0 0 100%'
    }
  },
  card: {
    maxWidth: theme.typography.pxToRem(318),
    margin: '0 auto',
    height: '100%',
    '&:hover': {
      boxShadow: '0 1px 8px 4px rgba(0, 0, 0, 0.20)',
      transform: 'scale(1.01, 1.01)'
    }
  },
  media: {
    height: theme.typography.pxToRem(168),
    maxWidth: '100%',
    marginBottom: theme.spacing(0.5),
    backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #EEEEEE 100%)'
  },
  title: {
    height: theme.typography.pxToRem(35)
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    paddingBottom: theme.spacing(2)
  },
  grey: { color: '#696969' }
}));

const useStatusBadgeStyles = makeStyles(theme => ({
  root: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    padding: '4px 8px',
    width: 'fit-content'
  },
  text: {
    fontSize: 14
  }
}));

export default AppCard;
